import { useCallback, useEffect, useState } from 'react';
import type { WearApiClient } from '../api/WearApiClient';
import type { AdaptcutTool } from '../type/WearTypes';

/** 'forbidden': the user lacks the Adaptcut module (403), the picker is hidden. */
export type AdaptcutToolsStatus = 'loading' | 'ok' | 'forbidden' | 'error';

/** Adaptcut tools for the cutting-diameter picker, loaded once per page. */
export function useAdaptcutTools(client: WearApiClient | null) {
  const [state, setState] = useState<{ status: AdaptcutToolsStatus; tools: AdaptcutTool[] }>({
    status: 'loading',
    tools: [],
  });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!client) return;
    const controller = new AbortController();
    client
      .adaptcutTools(controller.signal)
      .then((res) => {
        if (controller.signal.aborted) return;
        if (res.ok && Array.isArray(res.data?.output)) {
          setState({ status: 'ok', tools: res.data.output });
        } else if (!res.ok && res.code === 403) {
          setState({ status: 'forbidden', tools: [] });
        } else {
          setState({ status: 'error', tools: [] });
        }
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted || (error instanceof Error && error.name === 'AbortError'))
          return;
        setState({ status: 'error', tools: [] });
      });
    return () => controller.abort();
  }, [client, tick]);

  const retry = useCallback(() => {
    setState({ status: 'loading', tools: [] });
    setTick((value) => value + 1);
  }, []);

  return { ...state, retry };
}

/** Adaptcut tools whose name or reference equals the traceability reference (trimmed, any case). */
export function matchesReference(
  tool: AdaptcutTool,
  reference: string | null | undefined
): boolean {
  if (!reference) return false;
  const wanted = reference.trim().toLowerCase();
  return [tool.name, tool.reference].some((value) => value?.trim().toLowerCase() === wanted);
}
